import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What does 'finding harmony amid chaos' mean according to this module?",
    options: [
      "Avoiding all chaos and difficulties",
      "Discovering inner peace and balance even in turbulent times",
      "Giving up when things get chaotic",
      "Being in constant turmoil"
    ],
    correctAnswer: 1,
    explanation: "Finding harmony amid chaos means discovering inner peace and balance even in turbulent times. It's about maintaining equilibrium internally regardless of external circumstances."
  },
  {
    question: "According to the module, inner peace is:",
    options: [
      "The absence of all thoughts and emotions",
      "A state of calm acceptance and emotional balance",
      "Being numb to life's challenges",
      "Only possible in perfect circumstances"
    ],
    correctAnswer: 1,
    explanation: "Inner peace is a state of calm acceptance and emotional balance. It doesn't mean the absence of thoughts or emotions, but rather the ability to remain centered amidst life's ups and downs."
  },
  {
    question: "What is the relationship between inner peace and external circumstances?",
    options: [
      "Inner peace depends entirely on external conditions",
      "Inner peace can be maintained regardless of external chaos",
      "External peace is more important than inner peace",
      "Inner peace is impossible in difficult times"
    ],
    correctAnswer: 1,
    explanation: "Inner peace can be maintained regardless of external chaos. While we can't always control our circumstances, we can cultivate inner stability and calm through spiritual practices."
  },
  {
    question: "The module suggests cultivating inner peace through:",
    options: [
      "Only meditation and yoga",
      "Breathing exercises, acceptance practices, and mindful presence",
      "Avoiding all challenges",
      "Being constantly busy to distract from difficulties"
    ],
    correctAnswer: 1,
    explanation: "Inner peace can be cultivated through breathing exercises, acceptance practices, and mindful presence. These tools help you stay centered and peaceful even during challenging times."
  },
  {
    question: "What is the benefit of finding harmony amid chaos?",
    options: [
      "It makes you less resilient",
      "It provides stability and clarity during difficult times",
      "It prevents you from taking action",
      "It has no practical benefits"
    ],
    correctAnswer: 1,
    explanation: "Finding harmony amid chaos provides stability and clarity during difficult times. When you maintain inner peace, you can respond to challenges more effectively and make better decisions."
  }
];

const SpiritualModule6QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM6');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 6 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The nature of inner peace</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Finding harmony amid chaos</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Practices for maintaining balance</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by exploring spiritual growth in coaching practice!</p>
            <p>Ready to integrate spirituality into your professional work?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 6 Quiz</h1>
            <p className="module-subtitle">Spiritual Practices – Tools for Growth</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule6QuizPage;

